import { useEffect } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { COMMAND_PRIORITY_LOW, PASTE_COMMAND } from 'lexical';
import { DRAG_DROP_PASTE } from '@lexical/rich-text';
import {
  useInsertUploadedImage,
  type UploadImageFn,
} from './useInsertUploadedImage';

/**
 * Pulls image files off a clipboard/drag payload. Prefers `files` (screenshots,
 * file copies); falls back to `items` (images copied from a web page or app,
 * which sometimes only surface there).
 */
function getImageFiles(data: DataTransfer): File[] {
  const fromFiles = Array.from(data.files).filter((file) =>
    file.type.startsWith('image/'),
  );
  if (fromFiles.length > 0) return fromFiles;
  const fromItems: File[] = [];
  for (const item of Array.from(data.items)) {
    if (item.kind === 'file' && item.type.startsWith('image/')) {
      const file = item.getAsFile();
      if (file) fromItems.push(file);
    }
  }
  return fromItems;
}

/**
 * Uploads and inserts images pasted (Ctrl/Cmd+V or the right-click paste menu)
 * or dropped into the editor, routed through the same upload →
 * INSERT_IMAGE_COMMAND path as the toolbar button.
 *
 * Handling PASTE_COMMAND directly (rather than relying only on
 * DRAG_DROP_PASTE) is deliberate: the rich-text plugin dispatches
 * DRAG_DROP_PASTE only when the payload has files *and no text* — but images
 * copied from a web page or app usually arrive alongside `text/html`, so that
 * path silently skips them. We intercept the paste ourselves whenever an image
 * file is present, and fall through to the default paste otherwise.
 *
 * Only rendered when an `uploadImage` uploader is provided; without a durable
 * `documentId` an image can't survive HTML round-trips, so we don't accept it.
 */
export function ImagePastePlugin({
  uploadImage,
}: {
  uploadImage: UploadImageFn;
}): null {
  const [editor] = useLexicalComposerContext();
  const { insertImage } = useInsertUploadedImage(uploadImage);

  useEffect(() => {
    // If the editor unmounts mid-upload (e.g. the user navigates away right
    // after pasting), stop dispatching inserts/toasts for the rest of the
    // batch — the completed uploads are harmless but the work is wasted.
    let mounted = true;
    const insertAll = (files: File[]) => {
      // Upload sequentially so multiple images insert in order.
      void (async () => {
        for (const file of files) {
          if (!mounted) return;
          await insertImage(file);
        }
      })();
    };

    // Ctrl/Cmd+V and the right-click "Paste" menu both fire PASTE_COMMAND.
    const unregisterPaste = editor.registerCommand(
      PASTE_COMMAND,
      (event) => {
        if (!(event instanceof ClipboardEvent) || !event.clipboardData) {
          return false;
        }
        const images = getImageFiles(event.clipboardData);
        if (images.length === 0) return false;
        // Preempt the default paste (which would drop the image, since the
        // pasted <img> has no durable data-document-id) and upload instead.
        event.preventDefault();
        insertAll(images);
        return true;
      },
      COMMAND_PRIORITY_LOW,
    );
    // Drag-and-drop of an image file.
    const unregisterDrop = editor.registerCommand(
      DRAG_DROP_PASTE,
      (files) => {
        const images = files.filter((file) => file.type.startsWith('image/'));
        if (images.length === 0) return false;
        insertAll(images);
        return true;
      },
      COMMAND_PRIORITY_LOW,
    );

    return () => {
      mounted = false;
      unregisterPaste();
      unregisterDrop();
    };
  }, [editor, insertImage]);

  return null;
}
